import { StringFieldModel } from "@/common/commonForms";
import { TMessage } from "@/i18n/tt";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";
import { observer } from "mobx-react";
import { Box, VBox } from "./Box";
import { DCCallback, useTranslation } from "@/stores/RootStoreContext";
import { MTextInput } from "./TextInput";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";
import { Button, IconButton } from "./Button";
import Form from "./Form";
import { MTextArea } from "./TextArea";
import { PhoneInput, PhoneInputModel } from "./PhoneInput";
import { Tag, TagProps } from "@/components-v2/Tag.tsx";

export type EditableFieldProps = {
  label: string | TMessage;
  value: string;
  canEdit?: boolean;
  editModel?:
    | ["input", StringFieldModel]
    | ["textarea", StringFieldModel]
    | ["richtext", LocalizedTextEditorModel]
    | ["phone", PhoneInputModel];
  isSubmitting?: boolean;
  onEdit: DCCallback;
  onCancelEdit: DCCallback;
  onSubmitEdit: DCCallback;
  tag?: TagProps;
};

const renderEditor = (editModel: EditableFieldProps["editModel"]) => {
  if (!editModel) {
    return null;
  }

  switch (editModel[0]) {
    case "input":
      return <MTextInput model={editModel[1]} autofocus />;
    case "textarea":
      return <MTextArea model={editModel[1]} autofocus expandable />;
    case "richtext":
      return <LocalizedTextEditor model={editModel[1]} autofocus />;
    case "phone":
      return <PhoneInput model={editModel[1]} autoFocus hideLabel />;
  }
};

export const EditableField = observer((props: EditableFieldProps) => {
  const { t, tf } = useTranslation();
  return (
    <VBox>
      <div>{tf(props.label)}</div>
      {props.editModel ? (
        <Form
          onSubmit={props.onSubmitEdit}
          onCancel={props.onCancelEdit}
          loading={props.isSubmitting}
        >
          <VBox>
            {renderEditor(props.editModel)}
            <Box>
              <Button
                type={"submit"}
                variant={"primary"}
                label={t("save")}
                loading={props.isSubmitting}
              />
              <Button
                variant={"secondary"}
                label={t("cancel")}
                onClick={props.onCancelEdit}
                disabled={props.isSubmitting}
              />
            </Box>
          </VBox>
        </Form>
      ) : (
        <Box alignItems={"center"}>
          <FieldValue value={props.value} />
          {props.canEdit !== false && (
            <IconButton icon={"edit"} tooltip={t("settings.edit")} onClick={props.onEdit} compact />
          )}
          {props.tag && <Tag {...props.tag} />}
        </Box>
      )}
    </VBox>
  );
});

export const FieldValue = observer(({ value }: { value: string }) => {
  const { t } = useTranslation();
  return value ? <div>{value}</div> : <Box fg={"subtle"}>{t("not-set")}</Box>;
});
